import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Measures a container so charts can be drawn at real pixel sizes instead of a
 * stretched viewBox (which would distort labels and stroke widths).
 */
export function useElementWidth<T extends HTMLElement>(fallback = 640): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = (): void => {
      const next = Math.round(element.clientWidth);
      if (next > 0) setWidth(next);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
